import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  collectionGroup,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';

const db = getFirestore();

function toDateValue(ts) {
  try {
    if (!ts) return null;
    if (typeof ts?.toDate === 'function') return ts.toDate();
    if (typeof ts === 'number') return new Date(ts < 1000000000000 ? ts * 1000 : ts);
    if (typeof ts === 'string') {
      const n = Number(ts);
      if (Number.isFinite(n) && String(n) === ts.trim()) return new Date(n < 1000000000000 ? n * 1000 : n);
      return new Date(ts);
    }
    if (typeof ts === 'object' && typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    if (typeof ts === 'object' && typeof ts._seconds === 'number') return new Date(ts._seconds * 1000);
    return new Date(ts);
  } catch {
    return null;
  }
}

function fmtTs(ts) {
  try {
    const d = toDateValue(ts);
    if (!d || Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

function normalizeOrgName(o) {
  return o?.name || o?.displayName || o?.orgName || o?._archMeta?.name || o?.id || 'Untitled org';
}

function extractCollectionOrgId(path = '') {
  const parts = String(path).split('/');
  const idx = parts.indexOf('orgs');
  if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
  return '';
}

function getAliasStorageKey(orgId, deviceUid) {
  return `deviceAlias:${orgId || ''}:${deviceUid || ''}`;
}

function readLocalAlias(orgId, deviceUid) {
  try {
    if (!orgId || !deviceUid) return '';
    return String(window.localStorage.getItem(getAliasStorageKey(orgId, deviceUid)) || '').trim();
  } catch {
    return '';
  }
}

function buildRenameLookup(rows = []) {
  const map = new Map();
  rows.forEach((row) => {
    const orgId = row.orgId || '';
    const uid = row.deviceUid || '';
    if (!orgId || !uid) return;
    const name = String(row.deviceName || '').trim();
    if (!name) return;
    const key = `${orgId}:${uid}`;
    const prev = map.get(key);
    const nextTs = toDateValue(row.createdAt)?.getTime() || 0;
    const prevTs = toDateValue(prev?.createdAt)?.getTime() || 0;
    if (!prev || nextTs >= prevTs) map.set(key, { name, createdAt: row.createdAt });
  });
  return map;
}

function resolveDeviceDisplayName(row, renameLookup) {
  const orgId = row?.orgId || '';
  const uid = row?.deviceUid || '';
  const localAlias = readLocalAlias(orgId, uid);
  if (localAlias) return localAlias;
  const renamed = renameLookup.get(`${orgId}:${uid}`)?.name || '';
  if (renamed) return renamed;
  return row?.deviceName || row?.deviceUid || 'Unknown device';
}


function getLocalArchiveKey(type, orgId) {
  return `superArchive:${type}:${orgId || 'global'}`;
}

function readLocalArchive(type, orgId) {
  try {
    const raw = window.localStorage.getItem(getLocalArchiveKey(type, orgId));
    const rows = raw ? JSON.parse(raw) : [];
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function readAllLocalArchives(type, orgId) {
  const direct = readLocalArchive(type, orgId);
  const globalRows = readLocalArchive(type, '');
  return [...direct, ...globalRows];
}

function getQueryOrgId() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('orgId') || params.get('org') || '';
  } catch {
    return '';
  }
}

function dedupeBy(rows, pickId) {
  const map = new Map();
  rows.forEach((row, idx) => {
    const key = pickId(row, idx);
    if (!key || map.has(key)) return;
    map.set(key, row);
  });
  return [...map.values()];
}

function SectionCard({ title, desc, count, children, right, storageKey, defaultOpen = false }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(`superArchiveSection:${storageKey || title}`);
      if (saved === 'open') return true;
      if (saved === 'closed') return false;
    } catch {}
    return defaultOpen;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(`superArchiveSection:${storageKey || title}`, open ? 'open' : 'closed');
    } catch {}
  }, [open, storageKey, title]);

  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-4 py-4 border-b border-white/10 text-left"
      >
        <div>
          <div className="text-lg font-semibold text-white">{title}</div>
          <div className="text-sm text-white/60 mt-1">{desc}</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {right}
          <div className="px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/20 text-cyan-200 text-sm">{count}</div>
          <div className="w-8 h-8 rounded-full border border-white/10 bg-white/5 grid place-items-center text-white/70">{open ? '−' : '+'}</div>
        </div>
      </button>
      {open ? <div className="p-4">{children}</div> : null}
    </section>
  );
}

export default function SuperArchivePage() {
  const [search, setSearch] = useState('');
  const scopedOrgId = getQueryOrgId();
  const [archivedOrgs, setArchivedOrgs] = useState([]);
  const [deviceEvents, setDeviceEvents] = useState([]);
  const [killedDeviceRows, setKilledDeviceRows] = useState([]);
  const [deviceAliases, setDeviceAliases] = useState([]);
  const [userSnapshots, setUserSnapshots] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'archived_orgs'), orderBy('archivedAt', 'desc')),
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const meta = d.data() || {};
          const org = meta.org || meta.snapshot || {};
          rows.push({ id: d.id, ...org, _archMeta: meta });
        });
        setArchivedOrgs(rows);
      },
      (err) => {
        console.error('archived_orgs read failed', err);
        setArchivedOrgs([]);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collectionGroup(db, 'device_action_archive'), orderBy('createdAt', 'desc')),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            ...data,
            orgId: data.orgId || extractCollectionOrgId(d.ref.path),
          };
        });
        setDeviceEvents(rows);
      },
      (err) => {
        console.error('device_action_archive read failed', err);
        setDeviceEvents([]);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collectionGroup(db, 'killed_devices'), orderBy('killedAt', 'desc')),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            ...data,
            orgId: data.orgId || extractCollectionOrgId(d.ref.path),
          };
        });
        setKilledDeviceRows(rows);
      },
      (err) => {
        console.error('killed_devices read failed', err);
        setKilledDeviceRows([]);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collectionGroup(db, 'device_aliases'), orderBy('updatedAt', 'desc')),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            ...data,
            orgId: data.orgId || extractCollectionOrgId(d.ref.path),
            deviceUid: data.deviceUid || d.id,
          };
        });
        setDeviceAliases(rows);
      },
      (err) => {
        console.error('device_aliases read failed', err);
        setDeviceAliases([]);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collectionGroup(db, 'members')),
      (snap) => {
        const rows = snap.docs
          .map((d) => {
            const data = d.data() || {};
            return {
              id: d.id,
              ...data,
              orgId: data.orgId || extractCollectionOrgId(d.ref.path),
            };
          })
          .filter((m) => {
            const status = String(m.status || '').toLowerCase();
            return (
              m.archived === true ||
              m.isArchived === true ||
              m.deleted === true ||
              m.disabled === true ||
              status === 'archived' ||
              status === 'disabled' ||
              status === 'deleted' ||
              status === 'inactive'
            );
          });
        setUserSnapshots(rows);
      },
      (err) => {
        console.error('members group read failed', err);
        setUserSnapshots([]);
      }
    );
    return () => unsub();
  }, []);


  const localDeviceEvents = useMemo(() => readAllLocalArchives('device_action_archive', scopedOrgId), [scopedOrgId]);
  const localKilledDevices = useMemo(() => readAllLocalArchives('killed_devices', scopedOrgId), [scopedOrgId]);

  const allDeviceEvents = useMemo(() => {
    const merged = dedupeBy([
      ...deviceEvents,
      ...localDeviceEvents,
    ], (e, idx) => `${e.orgId || scopedOrgId}:${e.deviceUid || e.id || idx}:${e.type || 'event'}:${fmtTs(e.createdAt)}`);
    return merged.sort((a, b) => (toDateValue(b.createdAt)?.getTime() || 0) - (toDateValue(a.createdAt)?.getTime() || 0));
  }, [deviceEvents, localDeviceEvents, scopedOrgId]);

  const allKilledDevices = useMemo(() => {
    const merged = dedupeBy([
      ...killedDeviceRows,
      ...localKilledDevices,
    ], (e, idx) => `${e.orgId || scopedOrgId}:${e.deviceUid || e.id || idx}:${fmtTs(e.killedAt || e.archivedAt)}`);
    return merged.sort((a, b) => (toDateValue(b.killedAt || b.archivedAt)?.getTime() || 0) - (toDateValue(a.killedAt || a.archivedAt)?.getTime() || 0));
  }, [killedDeviceRows, localKilledDevices, scopedOrgId]);

  const aliasEvents = useMemo(() => {
    const fromActions = [...deviceEvents, ...localDeviceEvents].filter((e) => String(e.type || '').toLowerCase() === 'rename_device');
    const fromAliases = (deviceAliases || []).map((a) => ({
      orgId: a.orgId || '',
      deviceUid: a.deviceUid || a.id || '',
      deviceName: a.alias || '',
      createdAt: a.updatedAt || a.createdAt || null,
    }));
    return [...fromActions, ...fromAliases];
  }, [deviceEvents, localDeviceEvents, deviceAliases]);

  const renameLookup = useMemo(() => buildRenameLookup(aliasEvents), [aliasEvents]);

  const hydratedDeviceEvents = useMemo(() => allDeviceEvents.map((e) => ({
    ...e,
    resolvedDeviceName: resolveDeviceDisplayName(e, renameLookup),
  })), [allDeviceEvents, renameLookup]);

  const hydratedKilledDevices = useMemo(() => allKilledDevices.map((e) => ({
    ...e,
    resolvedDeviceName: resolveDeviceDisplayName(e, renameLookup),
  })), [allKilledDevices, renameLookup]);

  const term = search.trim().toLowerCase();

  const filteredOrgs = useMemo(() => {
    if (!term) return archivedOrgs;
    return archivedOrgs.filter((o) => {
      const hay = `${normalizeOrgName(o)} ${o.id || ''} ${o.defaultApp || ''} ${o.province || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [archivedOrgs, term]);

  const nonTerminalDeviceEvents = useMemo(() => {
    return hydratedDeviceEvents.filter((e) => !['kill_device', 'expire_session', 'session_expired'].includes(String(e.type || '').toLowerCase()));
  }, [hydratedDeviceEvents]);

  const filteredEvents = useMemo(() => {
    if (!term) return nonTerminalDeviceEvents;
    return nonTerminalDeviceEvents.filter((e) => {
      const hay = `${e.orgName || ''} ${e.orgId || ''} ${e.storeName || ''} ${e.resolvedDeviceName || e.deviceName || ''} ${e.deviceUid || ''} ${e.type || ''} ${e.reason || ''} ${e.actorEmail || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [nonTerminalDeviceEvents, term]);

  const killedDevices = useMemo(() => {
    if (!term) return hydratedKilledDevices;
    return hydratedKilledDevices.filter((e) => `${e.orgName || ''} ${e.orgId || ''} ${e.storeName || ''} ${e.resolvedDeviceName || e.deviceName || ''} ${e.deviceUid || ''} ${e.reason || ''} ${e.actorEmail || ''}`.toLowerCase().includes(term));
  }, [hydratedKilledDevices, term]);

  const filteredUsers = useMemo(() => {
    if (!term) return userSnapshots;
    return userSnapshots.filter((u) => {
      const hay = `${u.orgId || ''} ${u.email || ''} ${u.displayName || ''} ${u.role || ''} ${u.status || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [userSnapshots, term]);

  return (
    <div className="text-white space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold">Archives</h2>
          <p className="text-white/60 text-sm mt-1">One place to review archived orgs, device actions, and inactive member snapshots.</p>
          <p className="text-cyan-200/80 text-xs mt-2">Archive history now also reads a local browser mirror when Firestore archive writes are blocked.</p>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search org, device, reason, user…"
          className="w-full max-w-md rounded-xl bg-[#0b0f19] border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-cyan-400/40"
        />
      </div>

      <SectionCard title="Archived organizations" desc="Organizations restored or archived through super admin appear here." count={filteredOrgs.length} storageKey="archived_orgs" defaultOpen={false}>
        {filteredOrgs.length === 0 ? (
          <div className="text-white/60 text-sm">No archived organizations found.</div>
        ) : (
          <div className="space-y-3">
            {filteredOrgs.map((o) => (
              <div key={o.id} className="rounded-xl border border-white/10 bg-[#0b1220] px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-white">{normalizeOrgName(o)}</div>
                    <div className="text-xs text-white/50 mt-1">Org ID: {o.id}</div>
                  </div>
                  <div className="text-xs text-white/60">Archived: {fmtTs(o?._archMeta?.archivedAt)}</div>
                </div>
                <div className="grid md:grid-cols-3 gap-3 mt-3 text-sm text-white/70">
                  <div>Default app: <span className="text-white">{o.defaultApp || '—'}</span></div>
                  <div>Province: <span className="text-white">{o.province || '—'}</span></div>
                  <div>Archived by: <span className="text-white">{o?._archMeta?.archivedBy || o?._archMeta?.actorEmail || '—'}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Device action archive" desc="Rename and scheduled action events logged from the Download page." count={filteredEvents.length} storageKey="device_action_archive" defaultOpen={false}>
        {filteredEvents.length === 0 ? (
          <div className="text-white/60 text-sm">No device archive events logged yet.</div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map((e) => (
              <div key={`${e.orgId}-${e.id}`} className="rounded-xl border border-white/10 bg-[#0b1220] px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-cyan-400/20 bg-cyan-500/10 text-cyan-200">{String(e.type || 'device_event').replaceAll('_', ' ')}</span>
                    <span className="font-semibold text-white">{e.resolvedDeviceName || e.deviceName || e.deviceUid || 'Unknown device'}</span>
                  </div>
                  <div className="text-xs text-white/60 flex items-center gap-2">{e.source === 'local_mirror' ? <span className="px-2 py-0.5 rounded-full border border-amber-400/20 bg-amber-500/10 text-amber-200">Local mirror</span> : null}{fmtTs(e.createdAt)}</div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm text-white/70">
                  <div>Org: <span className="text-white">{e.orgName || e.orgId || '—'}</span></div>
                  <div>Store: <span className="text-white">{e.storeName || e.storeId || '—'}</span></div>
                  <div>Actor: <span className="text-white">{e.actorEmail || e.actorUid || '—'}</span></div>
                  <div>UID: <span className="text-white">{e.deviceUid || '—'}</span></div>
                </div>
                {(e.reason || e.impact || e.previousName) ? (
                  <div className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/75">
                    {e.reason ? <div>Reason: <span className="text-white">{e.reason}</span></div> : null}
                    {e.impact ? <div className="mt-1">Impact: <span className="text-white">{e.impact}</span></div> : null}
                    {e.previousName ? <div className="mt-1">Previous name: <span className="text-white">{e.previousName}</span></div> : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Killed devices" desc="Devices that were killed or expired and now belong in archive history." count={killedDevices.length} storageKey="killed_devices" defaultOpen={false}>
        {killedDevices.length === 0 ? (
          <div className="text-white/60 text-sm">No killed devices logged yet.</div>
        ) : (
          <div className="space-y-3">
            {killedDevices.map((e) => (
              <div key={`killed-${e.orgId}-${e.deviceUid || e.id}`} className="rounded-xl border border-white/10 bg-[#0b1220] px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-white">{e.resolvedDeviceName || e.deviceName || e.deviceUid || 'Unknown device'}</div>
                    <div className="text-xs text-white/50 mt-1">UID: {e.deviceUid || '—'}</div>
                  </div>
                  <div className="text-xs text-white/60 flex items-center gap-2">{e.source === 'local_mirror' ? <span className="px-2 py-0.5 rounded-full border border-amber-400/20 bg-amber-500/10 text-amber-200">Local mirror</span> : null}Killed: {fmtTs(e.killedAt || e.archivedAt)}</div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm text-white/70">
                  <div>Org: <span className="text-white">{e.orgName || e.orgId || '—'}</span></div>
                  <div>Store: <span className="text-white">{e.storeName || e.storeId || '—'}</span></div>
                  <div>Linked on: <span className="text-white">{fmtTs(e.linkedAt)}</span></div>
                  <div>Last used: <span className="text-white">{fmtTs(e.lastUsedAt)}</span></div>
                  <div>Action: <span className="text-white">{String(e.type || 'kill_device').replaceAll('_', ' ')}</span></div>
                  <div>By: <span className="text-white">{e.actorEmail || e.actorUid || '—'}</span></div>
                </div>
                {(e.reason || e.impact) ? (
                  <div className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/75">
                    {e.reason ? <div>Reason: <span className="text-white">{e.reason}</span></div> : null}
                    {e.impact ? <div className="mt-1">Impact: <span className="text-white">{e.impact}</span></div> : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Inactive user snapshots" desc="Users that appear disabled, archived, deleted, or inactive under org members." count={filteredUsers.length} storageKey="inactive_users" defaultOpen={false}>
        {filteredUsers.length === 0 ? (
          <div className="text-white/60 text-sm">No inactive or archived members found.</div>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map((u) => (
              <div key={`${u.orgId}-${u.id}`} className="rounded-xl border border-white/10 bg-[#0b1220] px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-white">{u.displayName || u.email || u.id}</div>
                    <div className="text-xs text-white/50 mt-1">Org: {u.orgId || '—'}</div>
                  </div>
                  <div className="px-2.5 py-1 rounded-full text-xs border border-white/10 bg-white/5 text-white/80">{u.status || (u.disabled ? 'disabled' : u.archived ? 'archived' : 'inactive')}</div>
                </div>
                <div className="grid md:grid-cols-3 gap-3 mt-3 text-sm text-white/70">
                  <div>Email: <span className="text-white">{u.email || '—'}</span></div>
                  <div>Role: <span className="text-white">{u.role || '—'}</span></div>
                  <div>Updated: <span className="text-white">{fmtTs(u.updatedAt || u.modifiedAt || u.createdAt)}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
